import { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { cashFlowsApi, liquidityApi } from '../api/client';

// ── Design tokens — matches the rest of the platform ──────────────────────
const C = {
  ivory: '#FAF7F2', cream: '#F3EDE4', parchment: '#EBE3D6', linen: '#E2D9CC',
  navy: '#1B2A4A', navyLight: '#2A3D66', navyMuted: '#3A5080', charcoal: '#2C2C2C',
  burgundy: '#7A2E3B', burgundyLight: '#944054', burgundyMuted: 'rgba(122,46,59,0.12)',
  gold: '#B8953E', goldLight: '#D4AF5C', goldMuted: 'rgba(184,149,62,0.15)',
  hunter: '#2D5F4A', hunterLight: '#3D7A60', hunterMuted: 'rgba(45,95,74,0.12)',
  ink: '#1A1A1A', textBody: '#3D3830', textMuted: '#7A7265', textLight: '#A69D91',
  border: '#D6CCBE', borderLight: '#E8E0D4', borderDark: '#C4B8A8',
};
const fonts = {
  display: "'Cormorant Garamond', Georgia, serif",
  body: "'Libre Baskerville', Georgia, serif",
  mono: "'IBM Plex Mono', 'Courier New', monospace",
  sans: "'Lato', 'Helvetica Neue', sans-serif",
};

const fmtDate = (d: any) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${m}-${day}-${y}`;
};

// ── Shared UI primitives ──────────────────────────────────────────────────
const Card = ({ children, style: s = {} }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 3, ...s }}>{children}</div>
);

const PageHeader = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div style={{ marginBottom: 28, borderBottom: `1px solid ${C.border}`, paddingBottom: 16 }}>
    <h1 style={{ fontSize: 28, fontFamily: fonts.display, fontWeight: 600, color: C.ink, margin: 0 }}>{title}</h1>
    <p style={{ fontSize: 14, fontFamily: fonts.body, color: C.textMuted, margin: '6px 0 0', fontStyle: 'italic' }}>{subtitle}</p>
  </div>
);

const SectionTitle = ({ children, sub }: { children: React.ReactNode; sub?: string }) => (
  <div style={{ marginBottom: 18 }}>
    <h2 style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, fontFamily: fonts.sans, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0, borderBottom: `2px solid ${C.navy}`, display: 'inline-block', paddingBottom: 4 }}>{children}</h2>
    {sub && <p style={{ fontSize: 13, color: C.textLight, margin: '6px 0 0', fontFamily: fonts.body, fontStyle: 'italic' }}>{sub}</p>}
  </div>
);

const Stat = ({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) => (
  <div style={{ padding: '18px 22px', borderRight: `1px solid ${C.borderLight}`, flex: 1, minWidth: 140 }}>
    <div style={{ fontSize: 10, color: C.textLight, letterSpacing: '0.1em', fontFamily: fonts.sans, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 600, color: accent || C.ink, fontFamily: fonts.display, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, fontFamily: fonts.sans }}>{sub}</div>}
  </div>
);

const AlertBox = ({ type, title, children }: { type: string; title?: string; children: React.ReactNode }) => {
  const s: Record<string, { bg: string; border: string; fg: string }> = {
    danger:  { bg: C.burgundyMuted, border: C.burgundy, fg: C.burgundy },
    warning: { bg: C.goldMuted,     border: C.gold,     fg: C.gold     },
    success: { bg: C.hunterMuted,   border: C.hunter,   fg: C.hunter   },
    info:    { bg: 'rgba(42,61,102,0.08)', border: C.navy, fg: C.navy  },
  };
  const t = s[type] ?? s.info;
  return (
    <div style={{ padding: '14px 18px', background: t.bg, borderRadius: 3, borderLeft: `3px solid ${t.border}`, marginBottom: 10 }}>
      {title && <div style={{ fontFamily: fonts.sans, fontSize: 13, fontWeight: 700, color: t.fg, marginBottom: 4 }}>{title}</div>}
      <div style={{ fontFamily: fonts.body, fontSize: 12, color: C.textBody, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
};

const TH = ({ children, align = 'left' }: { children?: React.ReactNode; align?: string }) => (
  <th style={{ fontSize: 10, fontWeight: 700, color: C.textLight, fontFamily: fonts.sans, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: align as any, padding: '10px 14px', borderBottom: `2px solid ${C.borderDark}`, background: C.cream }}>{children}</th>
);
const TD = ({ children, mono, accent, align = 'left', bold }: { children: React.ReactNode; mono?: boolean; accent?: string; align?: string; bold?: boolean }) => (
  <td style={{ fontSize: 13, color: accent || C.textBody, padding: '11px 14px', borderBottom: `1px solid ${C.borderLight}`, fontFamily: mono ? fonts.mono : fonts.sans, fontWeight: bold ? 600 : 400, textAlign: align as any, lineHeight: 1.4 }}>{children}</td>
);

const ChartTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 4, padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', fontFamily: fonts.sans, fontSize: 12 }}>
      <p style={{ color: C.textMuted, margin: '0 0 6px', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}>{label}</p>
      {payload.map((e: any, i: number) => (
        <p key={i} style={{ color: e.color || C.ink, margin: '2px 0', fontWeight: 500 }}>
          {e.name}: {fmtK(e.value)}
        </p>
      ))}
    </div>
  );
};

// ── Number formatting ─────────────────────────────────────────────────────
function fmtK(v: number | null | undefined): string {
  if (v == null) return '—';
  const abs = Math.abs(v);
  const neg = v < 0;
  const wrap = (s: string) => neg ? `(${s})` : s;
  if (abs >= 1_000_000) return wrap(`$${(abs / 1_000_000).toFixed(1)}M`);
  if (abs >= 1_000)     return wrap(`$${(abs / 1_000).toFixed(0)}K`);
  return wrap(`$${abs.toFixed(0)}`);
}
function fmtAxis(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
}

// ── Cash flow type labels ──────────────────────────────────────────────────
const CF_TYPE_LABELS: Record<string, string> = {
  income: 'Income', expense: 'Expense', distribution: 'Distribution',
  contribution: 'Contribution', tax_payment: 'Tax Payment',
};
const CF_FREQ_LABELS: Record<string, string> = {
  one_time: 'One-Time', monthly: 'Monthly', quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual', annual: 'Annual',
};

// ── Types ─────────────────────────────────────────────────────────────────
interface ProjectionYear {
  year: number; label: string;
  inflows: number; outflows: number; net: number;
  cumulativeCash: number; ltv: number; dscr: number;
}
interface StressYear { year: number; label: string; base: number; rateStress: number; reDecline: number; combined: number; }
interface KPIs { peakFacilityYear: string; peakFacilityBalance: number; year6Cash: number; minDscr: number; minDscrYear: string; peakLtv: number; peakLtvYear: string; }
interface CashFlow {
  id: string; name: string; cash_flow_type: string; amount: string;
  frequency: string; start_date: string; end_date: string | null;
  growth_rate: string; notes: string | null;
}

// ── Add / Edit modal ───────────────────────────────────────────────────────
const EMPTY_FORM = { name: '', cash_flow_type: 'income', amount: '', frequency: 'annual', start_date: new Date().getFullYear() + '-01-01', end_date: '', growth_rate: '0', notes: '' };

function CashFlowModal({ item, onSave, onClose }: { item: CashFlow | null; onSave: () => void; onClose: () => void }) {
  const [form, setForm] = useState(item ? {
    name: item.name, cash_flow_type: item.cash_flow_type, amount: item.amount,
frequency: item.frequency, start_date: String(item.start_date).slice(0, 10),
end_date: item.end_date ? String(item.end_date).slice(0, 10) : '', growth_rate: item.growth_rate, notes: item.notes ?? '',
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!form.name || !form.amount) { setError('Name and amount are required.'); return; }
    setSaving(true); setError('');
    try {
      const body = { ...form, amount: parseFloat(form.amount), growth_rate: parseFloat(form.growth_rate) || 0, end_date: form.end_date || null };
      if (item) {
        await cashFlowsApi.update(item.id, body);
      } else {
        await cashFlowsApi.create(body);
      }
      onSave();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 3, fontFamily: fonts.sans, fontSize: 13, color: C.ink, background: C.ivory, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 10, fontWeight: 700, color: C.textLight, fontFamily: fonts.sans, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 4, padding: 32, width: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 600, color: C.ink, margin: '0 0 24px' }}>{item ? 'Edit Cash Flow' : 'Add Cash Flow'}</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rental income — Hartford" />
          </div>
          <div>
            <label style={labelStyle}>Type</label>
            <select style={inputStyle} value={form.cash_flow_type} onChange={e => setForm(f => ({ ...f, cash_flow_type: e.target.value }))}>
              {Object.entries(CF_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Frequency</label>
            <select style={inputStyle} value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
              {Object.entries(CF_FREQ_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Amount ($)</label>
            <input style={inputStyle} type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
          </div>
          <div>
            <label style={labelStyle}>Annual Growth Rate (decimal)</label>
            <input style={inputStyle} type="number" step="0.001" value={form.growth_rate} onChange={e => setForm(f => ({ ...f, growth_rate: e.target.value }))} placeholder="0.03 = 3%" />
          </div>
          <div>
            <label style={labelStyle}>Start Date</label>
            <input style={inputStyle} type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>End Date (optional)</label>
            <input style={inputStyle} type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea style={{ ...inputStyle, height: 64, resize: 'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>

        {error && <div style={{ color: C.burgundy, fontFamily: fonts.sans, fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 3, border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, fontFamily: fonts.sans, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '9px 20px', borderRadius: 3, border: 'none', background: C.navy, color: C.ivory, fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function LiquidityRunway() {
  const [projection, setProjection]   = useState<ProjectionYear[]>([]);
  const [stress, setStress]           = useState<StressYear[]>([]);
  const [kpis, setKpis]               = useState<KPIs | null>(null);
  const [cashFlows, setCashFlows]     = useState<CashFlow[]>([]);
  const [loading, setLoading]         = useState(true);
  const [editItem, setEditItem]       = useState<CashFlow | null | 'new'>('new' as any);
  const [showModal, setShowModal]     = useState(false);
  const [horizon, setHorizon]         = useState(6);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, stressRes, cfRes] = await Promise.all([
        liquidityApi.projection(horizon),
        liquidityApi.stress(horizon),
        cashFlowsApi.list(),
      ]);
      setProjection(projRes.data.projection ?? []);
      setKpis(projRes.data.kpis ?? null);
      setStress(stressRes.data.scenarios ?? []);
      setCashFlows(cfRes.data.cashFlows ?? []);
    } catch (e) {
      console.error('fetchAll error', e);
    } finally {
      setLoading(false);
    }
  }, [horizon]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this cash flow?')) return;
    await cashFlowsApi.delete(id);
    fetchAll();
  };

  // ── Empty state (no cash flows entered yet) ──────────────────────────
  const hasData = projection.some(p => p.inflows > 0 || p.outflows > 0);

  // ── Deficit detection for stress alerts ─────────────────────────────
  const combinedDeficit = stress.some(s => s.combined < 0);
  const combinedDeficitYears = stress.filter(s => s.combined < 0).map(s => s.label).join(', ');

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <span style={{ fontFamily: fonts.sans, fontSize: 13, color: C.textMuted }}>Computing runway…</span>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Liquidity Runway" subtitle="Cash flow waterfall and leverage analysis over the planning horizon" />

      {/* ── Horizon selector ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[3, 6, 10, 15, 20].map(h => (
          <button key={h} onClick={() => setHorizon(h)} style={{
            padding: '8px 18px', borderRadius: 3, cursor: 'pointer', fontFamily: fonts.sans, fontSize: 12, fontWeight: 600,
            border: `1px solid ${horizon === h ? C.navy : C.border}`,
            background: horizon === h ? C.navy : C.ivory,
            color: horizon === h ? C.ivory : C.textMuted,
            transition: 'all 0.15s',
          }}>{h} Year</button>
        ))}
      </div>

      {/* ── KPI bar ── */}
      <Card style={{ display: 'flex', marginBottom: 24, overflow: 'hidden', flexWrap: 'wrap' }}>
        <Stat label="Peak Facility"   value={kpis ? fmtK(kpis.peakFacilityBalance) : '—'} sub={kpis?.peakFacilityYear ? `Year ${kpis.peakFacilityYear}` : 'No liabilities found'} accent={C.gold} />
        <Stat label={`Year ${horizon} Cash`} value={kpis ? fmtK(kpis.year6Cash) : '—'}    sub="End of horizon"           accent={C.hunter} />
        <Stat label="Min DSCR"        value={kpis?.minDscr ? `${kpis.minDscr}×` : '—'}     sub={kpis?.minDscrYear ? `Year ${kpis.minDscrYear}` : 'Enter cash flows'} accent={C.navy} />
        <Stat label="Peak LTV"        value={kpis?.peakLtv ? `${kpis.peakLtv}%` : '—'}     sub="vs 65% covenant threshold" />
      </Card>

      {!hasData && (
        <AlertBox type="info" title="No Cash Flows Entered">
          Add income and expense records below to generate projections. You can also import them via the Data Import module.
        </AlertBox>
      )}

      {/* ── Waterfall ── */}
      {hasData && (
        <Card style={{ padding: 24, marginBottom: 20 }}>
          <SectionTitle sub="Annual inflows vs outflows with net cash overlay">Cash Flow Waterfall</SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={projection} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
              <XAxis dataKey="label" tick={{ fill: C.textLight, fontSize: 12, fontFamily: fonts.sans }} axisLine={{ stroke: C.border }} />
              <YAxis tick={{ fill: C.textLight, fontSize: 11, fontFamily: fonts.sans }} axisLine={{ stroke: C.border }} tickFormatter={fmtAxis} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="inflows"  fill={C.hunter}  radius={[3,3,0,0]} name="Inflows"  barSize={28} fillOpacity={0.85} />
              <Bar dataKey="outflows" fill={C.burgundy} radius={[3,3,0,0]} name="Outflows" barSize={28} fillOpacity={0.75} />
              <Line type="monotone" dataKey="net" stroke={C.gold} strokeWidth={2.5} dot={{ fill: C.gold, r: 4, strokeWidth: 0 }} name="Net" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Stress + Coverage table ── */}
      {hasData && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <Card style={{ padding: 24 }}>
            <SectionTitle sub="Cumulative cash under adverse scenarios">Stress Testing</SectionTitle>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={stress}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
                <XAxis dataKey="label" tick={{ fill: C.textLight, fontSize: 12, fontFamily: fonts.sans }} axisLine={{ stroke: C.border }} />
                <YAxis tick={{ fill: C.textLight, fontSize: 11, fontFamily: fonts.sans }} axisLine={{ stroke: C.border }} tickFormatter={fmtAxis} />
                <Tooltip content={<ChartTip />} />
                <ReferenceLine y={0} stroke={C.burgundy} strokeDasharray="6 3" />
                <Line type="monotone" dataKey="base"        stroke={C.navy}      strokeWidth={2}   name="Base"      dot={false} />
                <Line type="monotone" dataKey="rateStress"  stroke={C.gold}      strokeWidth={1.5} name="+200bps"   dot={false} strokeDasharray="5 3" />
                <Line type="monotone" dataKey="reDecline"   stroke={C.navyMuted} strokeWidth={1.5} name="RE −20%"   dot={false} strokeDasharray="5 3" />
                <Line type="monotone" dataKey="combined"    stroke={C.burgundy}  strokeWidth={2}   name="Combined"  dot={false} />
              </LineChart>
            </ResponsiveContainer>
            {combinedDeficit && (
              <AlertBox type="danger" title="Combined Stress — Deficit Detected">
                Cash position goes negative in {combinedDeficitYears}. Consider hedging rate exposure or establishing a rate cap.
              </AlertBox>
            )}
            {!combinedDeficit && stress.length > 0 && (
              <AlertBox type="success" title="All Scenarios Positive">
                The combined stress scenario (rate shock + RE decline) stays above zero across the planning horizon.
              </AlertBox>
            )}
          </Card>

          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px 12px' }}><SectionTitle>Annual Coverage</SectionTitle></div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr><TH>Year</TH><TH align="right">Net</TH><TH align="right">Cum Cash</TH><TH align="right">LTV</TH><TH align="right">DSCR</TH></tr>
              </thead>
              <tbody>
                {projection.map((d, i) => (
                  <tr key={d.year} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                    <TD bold>{d.label}</TD>
                    <TD mono align="right" accent={d.net >= 0 ? C.hunter : C.burgundy}>{fmtK(d.net)}</TD>
                    <TD mono align="right">{fmtK(d.cumulativeCash)}</TD>
                    <TD mono align="right">{d.ltv > 0 ? `${d.ltv}%` : '—'}</TD>
                    <TD mono align="right" accent={d.dscr > 0 && d.dscr < 1.1 ? C.gold : C.ink}>
                      {d.dscr > 0 ? `${d.dscr}×` : '—'}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ── Cash Flow Manager ── */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionTitle sub="Income, expenses, distributions, and tax payments driving the projection">Cash Flow Records</SectionTitle>
          <button
            onClick={() => { setEditItem(null); setShowModal(true); }}
            style={{ padding: '8px 18px', borderRadius: 3, border: 'none', background: C.navy, color: C.ivory, fontFamily: fonts.sans, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            + Add Cash Flow
          </button>
        </div>

        {cashFlows.length === 0 ? (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: C.textMuted, fontFamily: fonts.body, fontSize: 13, fontStyle: 'italic' }}>
            No cash flow records yet. Add your first income or expense above.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr><TH>Name</TH><TH>Type</TH><TH align="right">Amount</TH><TH>Frequency</TH><TH>Growth</TH><TH>Start</TH><TH>End</TH><TH></TH></tr>
            </thead>
            <tbody>
              {cashFlows.map((cf, i) => (
                <tr key={cf.id} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                  <TD bold>{cf.name}</TD>
                  <TD>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 2, fontSize: 10, fontWeight: 700, fontFamily: fonts.sans, letterSpacing: '0.06em', textTransform: 'uppercase', background: cf.cash_flow_type === 'income' || cf.cash_flow_type === 'contribution' ? C.hunterMuted : C.burgundyMuted, color: cf.cash_flow_type === 'income' || cf.cash_flow_type === 'contribution' ? C.hunter : C.burgundy }}>
                      {CF_TYPE_LABELS[cf.cash_flow_type] ?? cf.cash_flow_type}
                    </span>
                  </TD>
                  <TD mono align="right">{fmtK(parseFloat(cf.amount))}</TD>
                  <TD>{CF_FREQ_LABELS[cf.frequency] ?? cf.frequency}</TD>
                  <TD mono>{parseFloat(cf.growth_rate) > 0 ? `+${(parseFloat(cf.growth_rate) * 100).toFixed(1)}%` : '—'}</TD>
                  <TD>{fmtDate(cf.start_date)}</TD>
                  <TD>{fmtDate(cf.end_date)}</TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { setEditItem(cf); setShowModal(true); }} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.navy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                      <button onClick={() => handleDelete(cf.id)} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
                    </div>
                  </TD>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── Add / Edit modal ── */}
      {showModal && (
        <CashFlowModal
          item={editItem as CashFlow | null}
          onSave={() => { setShowModal(false); fetchAll(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
